import React from "react";
import { Button } from "@clarkinc/zest-design-system/ui";
import { upperCaseFirst } from "@wss/utilities/strings";
import { Store } from "@wss/store";
import Icon from "~/common/Icons/Standard";
import { ChromeBarState, ViewSetting } from "./useChromeBarComponentStore";
import { sendGtmEvent } from "~/analytics/definitions";
import { cls } from "@wss/cls";

interface Props {
  chromeBarStore: Store<ChromeBarState>;
}

const ToggleView = ({ chromeBarStore }: Props) => {
  const activeView = chromeBarStore.useState((state) => state.view);

  const handleButtonClick = (view: ViewSetting) => {
    if (activeView === view) return;
    chromeBarStore.setState({ view });
    sendGtmEvent({
      event: "PLP",
      eventAction: `${view === "grid" ? "Grid" : "List"} View Click`,
    });
  };

  const baseClasses =
    "border-solid border box-border cursor-pointer inline-block text-center no-underline antialiased focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-2";

  const uiButtonClasses =
    "text-shadow-white-75 hover:bg-gray-50 border-gray-400 text-gray-800 focus-visible:outline-gray-800 align-middle font-semibold active:shadow-[0px_4px_2px_0px_rgba(0,0,0,.15)_inset]";

  const renderButton = (
    id: string,
    testId: string,
    viewType: ViewSetting,
    classes: string,
  ) => {
    const button = (
      <Button
        id={id}
        size="small"
        theme="headless"
        data-testid={testId}
        onClick={() => handleButtonClick(viewType)}
        className={cls(
          baseClasses,
          classes,
          activeView === viewType ? "bg-gray-200" : "bg-gray-100",
          uiButtonClasses,
        )}
        data-no-jquery
        aria-pressed={activeView === viewType}
        aria-label={`${viewType} view`}
      >
        <Icon
          name={viewType}
          width="16px"
          height="16px"
          className="block mx-auto align-middle"
        />
        <span className="text-xs text-gray-800 leading-3 ml-1 block">
          {upperCaseFirst(viewType)}
        </span>
      </Button>
    );

    return button;
  };

  return (
    <div className="inline-flex mr-2.5">
      {renderButton(
        "grid-view-button",
        "grid-view",
        "grid",
        "rounded-l mr-0 rounded-r-none py-1 px-2 flex items-center focus-visible:[position:relative] focus-visible:z-10",
      )}
      {renderButton(
        "list-view-button",
        "list-view",
        "list",
        "rounded-r ml-0 rounded-l-none border-l-0 py-1 px-2 flex items-center",
      )}
    </div>
  );
};

export default ToggleView;
